import { Button } from '@/components/ui/button';
import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import PropertiesFilters from '@/components/v2/molecules/admin/tabs/AdminPropertiesTab/PropertiesFilters';
import PropertiesTable from '@/components/v2/molecules/admin/tabs/AdminPropertiesTab/PropertiesTable';
import { useProperties } from '@/hooks/useProperties';
import { Property } from '@/types/property';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useRolePermissions from '@/hooks/admin/shared/useRolePermissions';
import { parse } from 'date-fns';
import AdminPageHeader from '@/components/v2/atoms/admin/layout/AdminPageHeader';

const AdminPropertiesTab = () => {
  const { canCreate, canDelete } = useRolePermissions();
  const navigate = useNavigate();

  const {
    data: properties = [],
    isLoading: isLoadingProperties,
    error: errorProperties,
    refetch: refetchProperties,
  } = useProperties();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredProperties = properties
    .filter((property) => {
      const locationDisplay = property.neighborhood
        ? `${property.neighborhood.city}, ${property.neighborhood.state}`
        : '';
      const matchesSearch =
        property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        locationDisplay.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'available' && property.available) ||
        (statusFilter === 'unavailable' && !property.available) ||
        (statusFilter === 'featured' && property.featured);

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      // Sort by operation start date (oldest first)
      if (!a.operationStartedAt) return 1;
      if (!b.operationStartedAt) return -1;
      const aTime = parse(a.operationStartedAt, "yyyy-MM-dd", new Date()).getTime();
      const bTime = parse(b.operationStartedAt, "yyyy-MM-dd", new Date()).getTime();
      return aTime - bTime;
    });

  const handleEdit = (property: Property) => {
    navigate(`/admin/properties/edit/${property.id}`);
  };

  if (errorProperties) {
    return (
      <TabErrorBanner
        title="Error al cargar propiedades"
        message="No se pudieron cargar las propiedades. Por favor, intenta nuevamente."
        onRetry={() => refetchProperties()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Propiedades</h2>

        {canCreate && (
          <Button
            className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
            onClick={() => navigate('/admin/properties/create')}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nueva Propiedad
          </Button>
        )}
      </div>

      <PropertiesFilters
        onSearchChange={setSearchTerm}
        onStatusChange={setStatusFilter}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
      />

      {/* Properties Table */}
      {isLoadingProperties ? (
        <div className="space-y-4 border rounded-lg p-6">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="bg-gray-200 rounded h-12 animate-pulse"
            />
          ))}
        </div>
      ) : filteredProperties.length === 0 && (searchTerm || statusFilter !== 'all') ? (
        <div className="text-center py-12 border rounded-lg">
          <p className="text-gray-600 mb-4">
            No se encontraron propiedades con los filtros aplicados
          </p>
          <Button
            variant="outline"
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
            }}
          >
            Limpiar búsqueda
          </Button>
        </div>
      ) : (
        <PropertiesTable 
          properties={filteredProperties}
          onEdit={handleEdit}
          canDelete={canDelete}
        />
      )}
    </div>
  );
};

export default AdminPropertiesTab;
